import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  User, 
  Heart, 
  FileText, 
  CreditCard, 
  Plus, 
  Eye, 
  Download, 
  Trash2,
  Upload,
  Phone,
  Mail,
  Calendar,
  MapPin
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { downloadGradeReport } from '../lib/downloadGradeReport';
import ReportPeriodSelect from './ReportPeriodSelect';
import ParentEmailEditor from './ParentEmailEditor';

// Helper function to handle validation errors
const handleValidationError = (error) => {
  if (error.response?.data?.detail) {
    if (Array.isArray(error.response.data.detail)) {
      // Handle Pydantic validation errors
      const messages = error.response.data.detail.map(err => `${err.loc?.join('.')} - ${err.msg}`).join('\n');
      return messages;
    } else {
      return error.response.data.detail;
    }
  }
  return 'Error desconocido';
};

const StudentDetailsDialog = ({ student, open, onClose, onUpdate }) => {
  const [activeTab, setActiveTab] = useState('info');
  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [reportYears, setReportYears] = useState([]);
  const [reportYear, setReportYear] = useState('');
  const [reportPeriod, setReportPeriod] = useState('10');
  const [reportTeacher, setReportTeacher] = useState('');
  const [downloadingReport, setDownloadingReport] = useState(false);
  
  // Student form state
  const [studentForm, setStudentForm] = useState({
    first_name: '',
    middle_name: '',
    last_name_paternal: '',
    last_name_maternal: '',
    birth_date: '',
    ssn_last_4: '',
    father_full_name: '',
    mother_full_name: '',
    phone_number: '',
    residential_address: '',
    postal_address: '',
    email: '',
    assigned_grade: '',
    medical_conditions: [],
    emergency_contact_name: '',
    emergency_contact_phone: '',
    profile_photo_url: null
  });

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    payment_type: 'tuition',
    amount: '',
    due_date: '',
    payment_method: 'cash',
    description: ''
  });

  const grades = ['Preescolar', 'Kinder', '1ro', '2do', '3ro', '4to', '5to', '6to', '7mo', '8vo', '9no', '10mo', '11vo', '12vo'];

  useEffect(() => {
    if (student && open) {
      setReportYear('');
      setReportPeriod('10');
      setReportTeacher('');
      setReportYears([]);
      axios.get('/academic-years').then(({data}) => setReportYears(data)).catch(() => {});
      setStudentForm({
        ...student,
        birth_date: student.birth_date ? new Date(student.birth_date).toISOString().split('T')[0] : '',
        medical_conditions: student.medical_conditions || [],
        profile_photo_url: student.profile_photo_url || null
      });
      fetchPayments();
      fetchDocuments();
    }
  }, [student, open]);

  const fetchPayments = async () => {
    if (!student?.id) return;
    
    try {
      const response = await axios.get(`/payments/student/${student.id}`);
      setPayments(response.data);
    } catch (error) {
      console.error('Error fetching payments:', error);
    }
  };

  const fetchDocuments = async () => {
    if (!student?.id) return;
    
    try {
      const response = await axios.get(`/documents/student/${student.id}`);
      setDocuments(response.data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleSaveStudent = async () => {
    setLoading(true);
    try {
      const updateData = {
        ...studentForm
      };
      
      // Only convert birth_date if it exists and is not empty
      if (studentForm.birth_date) {
        // Ensure the date is in YYYY-MM-DD format first
        const dateStr = studentForm.birth_date.includes('T') 
          ? studentForm.birth_date.split('T')[0] 
          : studentForm.birth_date;
        updateData.birth_date = new Date(dateStr + 'T00:00:00Z').toISOString();
      }
      
      await axios.put(`/students/${student.id}`, updateData);
      toast.success('Estudiante actualizado exitosamente');
      setEditMode(false);
      onUpdate?.();
    } catch (error) {
      console.error('Error updating student:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePayment = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post('/payments', {
        ...paymentForm,
        student_id: student.id,
        amount: parseFloat(paymentForm.amount),
        due_date: new Date(paymentForm.due_date).toISOString()
      });
      
      toast.success('Pago registrado exitosamente');
      setShowPaymentDialog(false);
      setPaymentForm({
        payment_type: 'tuition',
        amount: '',
        due_date: '',
        payment_method: 'cash',
        description: ''
      });
      fetchPayments();
    } catch (error) {
      console.error('Error creating payment:', error);
      toast.error(handleValidationError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingDoc(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'other');
      formData.append('description', `Documento subido para ${student.first_name} ${student.last_name_paternal}`);

      await axios.post(`/documents/upload/${student.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Documento subido exitosamente');
      fetchDocuments();
      e.target.value = ''; // Reset file input
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error(handleValidationError(error));
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDownloadDocument = async (documentId, filename) => {
    try {
      const response = await axios.get(`/documents/${documentId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading document:', error);
      toast.error('Error al descargar documento');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (window.confirm('¿Está seguro de que desea eliminar este documento?')) {
      try {
        await axios.delete(`/documents/${documentId}`);
        toast.success('Documento eliminado exitosamente');
        fetchDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
        toast.error('Error al eliminar documento');
      }
    }
  };

  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'status-paid';
      case 'pending': return 'status-pending';
      case 'overdue': return 'status-overdue';
      case 'partial': return 'payment-partial';
      default: return 'status-pending';
    }
  };

  const addMedicalCondition = () => {
    setStudentForm({
      ...studentForm,
      medical_conditions: [
        ...studentForm.medical_conditions,
        { condition: '', severity: 'mild', medications: '', notes: '', doctor_contact: '' }
      ]
    });
  };

  const updateMedicalCondition = (index, field, value) => {
    const updated = [...studentForm.medical_conditions];
    updated[index] = { ...updated[index], [field]: value };
    setStudentForm({ ...studentForm, medical_conditions: updated });
  };

  const removeMedicalCondition = (index) => {
    const updated = studentForm.medical_conditions.filter((_, i) => i !== index);
    setStudentForm({ ...studentForm, medical_conditions: updated });
  };

  if (!student) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden">
        <DialogHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                {student.first_name} {student.last_name_paternal} {student.last_name_maternal}
              </DialogTitle>
              <DialogDescription>
                Información completa del estudiante - Grado: {student.assigned_grade}
              </DialogDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={downloadingReport}
                onClick={async () => {
                  setDownloadingReport(true);
                  try {
                    await downloadGradeReport(student, { academicYear: reportYear, teacherName: reportTeacher, period: reportPeriod });
                    toast.success('Boleta descargada exitosamente');
                  } catch (error) {
                    toast.error(error.message);
                  } finally { setDownloadingReport(false); }
                }}
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <Download className="w-4 h-4 mr-2" />
                {downloadingReport ? 'Preparando PDF…' : 'Descargar Boleta'}
              </Button>
              
              <Button
                onClick={async () => {
                  try {
                    const response = await axios.get(`/students/${student.id}/transcript`, {
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `transcripcion_${student.student_number || student.id}.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Transcripción de créditos descargada exitosamente');
                  } catch (error) {
                    console.error('Error downloading transcript:', error);
                    toast.error('Error al descargar la transcripción');
                  }
                }}
                className="bg-blue-600 hover:bg-blue-700"
                size="sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Transcripción de Créditos
              </Button>
            </div>
          </div>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-md border p-3">
          <div>
            <Label htmlFor="report-school-year">Año de la boleta</Label>
            <select id="report-school-year" className="w-full border rounded-md p-2 text-sm bg-white mt-1" value={reportYear} onChange={e => setReportYear(e.target.value)}>
              <option value="">Año escolar actual</option>
              {[...new Set(reportYears.map(y => y.year_name))].map(year => <option key={year} value={year}>{year}</option>)}
            </select>
          </div>
          <div>
            <ReportPeriodSelect id="student-download-period" value={reportPeriod} onChange={setReportPeriod} disabled={downloadingReport} />
            <Label htmlFor="report-teacher-name">Maestro (solo si no hay salón hogar asignado)</Label>
            <Input id="report-teacher-name" className="mt-1" value={reportTeacher} maxLength={120} onChange={e => setReportTeacher(e.target.value)} placeholder="Nombre del maestro de grupo" />
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="info" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="hidden sm:inline">Información</span>
            </TabsTrigger>
            <TabsTrigger value="medical" className="flex items-center gap-2">
              <Heart className="w-4 h-4" />
              <span className="hidden sm:inline">Médico</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              <span className="hidden sm:inline">Pagos</span>
            </TabsTrigger>
            <TabsTrigger value="documents" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              <span className="hidden sm:inline">Documentos</span>
            </TabsTrigger>
          </TabsList>

          <div className="overflow-y-auto max-h-[60vh] mt-4">
            <TabsContent value="info" className="space-y-4">
              <ParentEmailEditor key={student.id} studentId={student.id} />
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Información Personal</h3>
                <Button
                  onClick={() => editMode ? handleSaveStudent() : setEditMode(true)}
                  disabled={loading}
                  data-testid="edit-student-button"
                >
                  {editMode ? (loading ? 'Guardando...' : 'Guardar') : 'Editar'}
                </Button>
              </div>

              {/* Profile Photo Section */}
              <Card className="bg-gradient-to-br from-blue-50 to-indigo-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-6">
                    <div className="relative">
                      <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-white shadow-lg bg-gray-200 flex items-center justify-center">
                        {studentForm.profile_photo_url ? (
                          <img
                            src={`${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}/api/students/${student.id}/photo`}
                            alt={`${student.first_name} ${student.last_name_paternal}`}
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              console.error('Error loading photo');
                              e.target.style.display = 'none';
                            }}
                          />
                        ) : (
                          <User className="w-16 h-16 text-gray-400" />
                        )}
                      </div>
                      <label 
                        htmlFor="photo-upload" 
                        className={`absolute bottom-0 right-0 bg-blue-600 hover:bg-blue-700 text-white rounded-full p-2 shadow-lg transition-colors ${uploadingPhoto ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                      >
                        <Upload className="w-4 h-4" />
                        <input
                          id="photo-upload"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          disabled={uploadingPhoto}
                          onChange={async (e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            
                            setUploadingPhoto(true);
                            try {
                              const formData = new FormData();
                              formData.append('file', file);
                              
                              await axios.post(`/students/${student.id}/upload-photo`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              
                              toast.success('Foto subida exitosamente');
                              
                              // Llamar onUpdate para refrescar los datos del estudiante
                              if (onUpdate) {
                                onUpdate();
                              }
                            } catch (error) {
                              console.error('Error uploading photo:', error);
                              toast.error('Error al subir la foto');
                            } finally {
                              setUploadingPhoto(false);
                              e.target.value = ''; // Reset file input
                            }
                          }}
                        />
                      </label>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold text-gray-900">
                        {student.first_name} {student.last_name_paternal}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">Número: {student.student_number}</p>
                      <p className="text-sm text-gray-600">Grado: {student.assigned_grade}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        {uploadingPhoto ? 'Subiendo foto...' : 'Click en el ícono de cámara para cambiar la foto'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label>Nombre</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.first_name}
                      onChange={(e) => setStudentForm({...studentForm, first_name: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.first_name}</p>
                  )}
                </div>

                <div>
                  <Label>Segundo Nombre</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.middle_name || ''}
                      onChange={(e) => setStudentForm({...studentForm, middle_name: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.middle_name || 'N/A'}</p>
                  )}
                </div>

                <div>
                  <Label>Apellido Paterno</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.last_name_paternal}
                      onChange={(e) => setStudentForm({...studentForm, last_name_paternal: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.last_name_paternal}</p>
                  )}
                </div>

                <div>
                  <Label>Apellido Materno</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.last_name_maternal}
                      onChange={(e) => setStudentForm({...studentForm, last_name_maternal: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.last_name_maternal}</p>
                  )}
                </div>

                <div>
                  <Label>Fecha de Nacimiento</Label>
                  {editMode ? (
                    <Input
                      type="date"
                      value={studentForm.birth_date}
                      onChange={(e) => setStudentForm({...studentForm, birth_date: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">
                      {new Date(student.birth_date).toLocaleDateString()}
                    </p>
                  )}
                </div>

                <div>
                  <Label>Últimos 4 dígitos SSN</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.ssn_last_4}
                      maxLength="4"
                      onChange={(e) => setStudentForm({...studentForm, ssn_last_4: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">****{student.ssn_last_4}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Nombre del Padre</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.father_full_name}
                      onChange={(e) => setStudentForm({...studentForm, father_full_name: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.father_full_name}</p>
                  )}
                </div>

                <div>
                  <Label>Nombre de la Madre</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.mother_full_name}
                      onChange={(e) => setStudentForm({...studentForm, mother_full_name: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.mother_full_name}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Phone className="w-4 h-4" />
                    Teléfono
                  </Label>
                  {editMode ? (
                    <Input
                      value={studentForm.phone_number}
                      onChange={(e) => setStudentForm({...studentForm, phone_number: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.phone_number}</p>
                  )}
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Email
                  </Label>
                  {editMode ? (
                    <Input
                      type="email"
                      value={studentForm.email}
                      onChange={(e) => setStudentForm({...studentForm, email: e.target.value})}
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.email}</p>
                  )}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Dirección Residencial
                </Label>
                {editMode ? (
                  <Textarea
                    value={studentForm.residential_address}
                    onChange={(e) => setStudentForm({...studentForm, residential_address: e.target.value})}
                  />
                ) : (
                  <p className="p-2 bg-gray-50 rounded">{student.residential_address}</p>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Contacto de Emergencia</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.emergency_contact_name || ''}
                      onChange={(e) => setStudentForm({...studentForm, emergency_contact_name: e.target.value})}
                      placeholder="Nombre del contacto"
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.emergency_contact_name || 'No registrado'}</p>
                  )}
                </div>

                <div>
                  <Label>Teléfono de Emergencia</Label>
                  {editMode ? (
                    <Input
                      value={studentForm.emergency_contact_phone || ''}
                      onChange={(e) => setStudentForm({...studentForm, emergency_contact_phone: e.target.value})}
                      placeholder="Teléfono del contacto"
                    />
                  ) : (
                    <p className="p-2 bg-gray-50 rounded">{student.emergency_contact_phone || 'No registrado'}</p>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="medical" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Condiciones Médicas</h3>
                {editMode && (
                  <Button onClick={addMedicalCondition} size="sm">
                    <Plus className="w-4 h-4 mr-2" />
                    Agregar Condición
                  </Button>
                )}
              </div>

              {studentForm.medical_conditions.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No hay condiciones médicas registradas</p>
              ) : (
                <div className="space-y-4">
                  {studentForm.medical_conditions.map((condition, index) => (
                    <Card key={index} className="p-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Condición</Label>
                          {editMode ? (
                            <Input
                              value={condition.condition}
                              onChange={(e) => updateMedicalCondition(index, 'condition', e.target.value)}
                              placeholder="Ej: Asma, Diabetes"
                            />
                          ) : (
                            <p className="p-2 bg-gray-50 rounded">{condition.condition}</p>
                          )}
                        </div>

                        <div>
                          <Label>Severidad</Label>
                          {editMode ? (
                            <Select
                              value={condition.severity}
                              onValueChange={(value) => updateMedicalCondition(index, 'severity', value)}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="mild">Leve</SelectItem>
                                <SelectItem value="moderate">Moderada</SelectItem>
                                <SelectItem value="severe">Severa</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <p className="p-2 bg-gray-50 rounded">
                              {condition.severity === 'mild' ? 'Leve' : 
                               condition.severity === 'moderate' ? 'Moderada' : 'Severa'}
                            </p>
                          )}
                        </div>

                        <div className="md:col-span-2">
                          <Label>Medicamentos</Label>
                          {editMode ? (
                            <Textarea
                              value={condition.medications || ''}
                              onChange={(e) => updateMedicalCondition(index, 'medications', e.target.value)}
                              placeholder="Medicamentos prescritos"
                            />
                          ) : (
                            <p className="p-2 bg-gray-50 rounded">{condition.medications || 'Ninguno'}</p>
                          )}
                        </div>

                        <div>
                          <Label>Contacto del Doctor</Label>
                          {editMode ? (
                            <Input
                              value={condition.doctor_contact || ''}
                              onChange={(e) => updateMedicalCondition(index, 'doctor_contact', e.target.value)}
                              placeholder="Teléfono del médico"
                            />
                          ) : (
                            <p className="p-2 bg-gray-50 rounded">{condition.doctor_contact || 'No registrado'}</p>
                          )}
                        </div>

                        {editMode && (
                          <div className="flex items-end">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => removeMedicalCondition(index)}
                              className="text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        )}
                      </div>

                      <div className="mt-4">
                        <Label>Notas Adicionales</Label>
                        {editMode ? (
                          <Textarea
                            value={condition.notes || ''}
                            onChange={(e) => updateMedicalCondition(index, 'notes', e.target.value)}
                            placeholder="Notas adicionales sobre la condición"
                          />
                        ) : (
                          <p className="p-2 bg-gray-50 rounded">{condition.notes || 'Sin notas'}</p>
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="payments" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Historial de Pagos</h3>
                <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
                  <Button onClick={() => setShowPaymentDialog(true)} data-testid="add-payment-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Nuevo Pago
                  </Button>
                  <DialogContent className="max-w-md">
                    <DialogHeader>
                      <DialogTitle>Registrar Pago</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleCreatePayment} className="space-y-4">
                      <div>
                        <Label>Tipo de Pago</Label>
                        <Select
                          value={paymentForm.payment_type}
                          onValueChange={(value) => setPaymentForm({...paymentForm, payment_type: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="tuition">Colegiatura</SelectItem>
                            <SelectItem value="enrollment">Matrícula</SelectItem>
                            <SelectItem value="books">Libros</SelectItem>
                            <SelectItem value="supplies">Materiales</SelectItem>
                            <SelectItem value="transport">Transporte</SelectItem>
                            <SelectItem value="food">Alimentación</SelectItem>
                            <SelectItem value="activities">Actividades</SelectItem>
                            <SelectItem value="other">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Monto</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={paymentForm.amount}
                          onChange={(e) => setPaymentForm({...paymentForm, amount: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label>Método de Pago</Label>
                        <Select
                          value={paymentForm.payment_method}
                          onValueChange={(value) => setPaymentForm({...paymentForm, payment_method: value})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="cash">💵 Efectivo</SelectItem>
                            <SelectItem value="card">💳 Tarjeta</SelectItem>
                            <SelectItem value="transfer">🏦 Transferencia</SelectItem>
                            <SelectItem value="stripe">🌐 Stripe</SelectItem>
                            <SelectItem value="other">📝 Otro</SelectItem>
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500 mt-1">
                          {['cash', 'transfer', 'other'].includes(paymentForm.payment_method) 
                            ? '✅ Este pago se marcará como completado automáticamente' 
                            : 'ℹ️ Este pago quedará pendiente hasta confirmar'}
                        </p>
                      </div>

                      <div>
                        <Label>Fecha de Vencimiento</Label>
                        <Input
                          type="date"
                          value={paymentForm.due_date}
                          onChange={(e) => setPaymentForm({...paymentForm, due_date: e.target.value})}
                          required
                        />
                      </div>

                      <div>
                        <Label>Descripción</Label>
                        <Textarea
                          value={paymentForm.description}
                          onChange={(e) => setPaymentForm({...paymentForm, description: e.target.value})}
                          placeholder="Descripción del pago"
                        />
                      </div>

                      <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                          Cancelar
                        </Button>
                        <Button type="submit" disabled={loading}>
                          {loading ? 'Registrando...' : 'Registrar Pago'}
                        </Button>
                      </div>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-3">
                {payments.map((payment) => (
                  <Card key={payment.id} className="p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium capitalize">
                            {payment.payment_type.replace('_', ' ')}
                          </h4>
                          <Badge className={getPaymentStatusColor(payment.status)}>
                            {payment.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600">{payment.description}</p>
                        <p className="text-sm text-gray-500">
                          Vence: {new Date(payment.due_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right">
                          <p className="text-lg font-bold">${payment.amount}</p>
                          {payment.paid_date && (
                            <p className="text-sm text-green-600">
                              Pagado: {new Date(payment.paid_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (window.confirm('¿Estás seguro de eliminar este pago?')) {
                              try {
                                await axios.delete(`/payments/${payment.id}`);
                                toast.success('Pago eliminado exitosamente');
                                fetchPayments();
                              } catch (error) {
                                console.error('Error deleting payment:', error);
                                toast.error('Error al eliminar el pago');
                              }
                            }
                          }}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
                {payments.length === 0 && (
                  <p className="text-gray-500 text-center py-8">No hay pagos registrados</p>
                )}
              </div>
            </TabsContent>

            <TabsContent value="documents" className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold">Documentos</h3>
                <div className="flex gap-2">
                  <input
                    type="file"
                    id="document-upload"
                    className="hidden"
                    onChange={handleFileUpload}
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  />
                  <Button 
                    onClick={() => document.getElementById('document-upload').click()}
                    disabled={uploadingDoc}
                    data-testid="upload-document-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadingDoc ? 'Subiendo...' : 'Subir Documento'}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {documents.map((doc) => (
                  <Card key={doc.id} className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4 text-blue-600" />
                      <h4 className="font-medium text-sm truncate">{doc.original_name}</h4>
                    </div>
                    <p className="text-xs text-gray-500 mb-2">
                      {doc.description || 'Sin descripción'}
                    </p>
                    <p className="text-xs text-gray-400 mb-3">
                      Subido: {new Date(doc.uploaded_at).toLocaleDateString()}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDownloadDocument(doc.id, doc.original_name)}
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteDocument(doc.id)}
                        className="text-red-600"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </Card>
                ))}
                {documents.length === 0 && (
                  <div className="col-span-full text-center py-8 text-gray-500">
                    <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                    <p>No hay documentos subidos</p>
                  </div>
                )}
              </div>
            </TabsContent>
          </div>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default StudentDetailsDialog;
